import React, { FC, MutableRefObject, ReactNode, useRef } from 'react';
import { Formik, FormikProps, useFormik } from 'formik';
import moment from 'moment/moment';
import { cloneDeep } from 'lodash';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  ContentFragment,
  ContentInput,
  Network,
  ShareContentMutation,
  ShareContentMutationVariables,
  SharedStatus,
  ShareType,
  useManualPostMutation,
  useShareContentMutation,
} from 'api';
import * as paths from 'paths';
import { useNotify, useProtectedClient, useSharePhoto } from 'hooks';

import { Share } from 'features/sharing/types';
import { sharingSchema } from '../../validations';

export interface ExternalShareRenderProps {
  isLoading: boolean;
  onSubmit: () => void;
  postFormikRef: MutableRefObject<FormikProps<ContentInput>>;
  shareFormikRef: MutableRefObject<FormikProps<Share>>;
}

interface Props {
  initialPostValues: ContentInput;
  children: (props: ExternalShareRenderProps) => ReactNode;
}

const Form: FC<Props> = ({ children, initialPostValues }) => {
  const notify = useNotify();
  const client = useProtectedClient();
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedPhotoIndex } = useSharePhoto();

  const initialPostFormikRef = useFormik({
    onSubmit: () => undefined,
    initialValues: initialPostValues,
  });

  const initialShareFormikRef = useFormik({
    onSubmit: () => undefined,
    initialValues: {
      items: [],
    },
  });

  const postFormikRef = useRef<FormikProps<ContentInput>>(initialPostFormikRef);
  const shareFormikRef = useRef<FormikProps<Share>>(initialShareFormikRef);

  const { mutate: shareContent, isLoading: isShareContentLoading } = useShareContentMutation({
    onError: notify.mutationError,
    onSuccess: handleCompleted,
  });

  const { mutate: processContent, isLoading: isProcessContentLoading } = useManualPostMutation({
    onError: notify.mutationError,
    onSuccess: ({ processContent }) => {
      shareToTwitterAndLinkedin(processContent);
      shareToFacebook(processContent);
    },
  });

  const isLoading = isShareContentLoading || isProcessContentLoading;

  function handleCompleted(data: ShareContentMutation) {
    const { results } = data.shareContent;
    const hasFailure = results.some(item => item.status === SharedStatus.Failed);

    if (hasFailure) {
      notify.error({ i18n: { i18nKey: 'sharing:shareFailure' } });
      return;
    }

    const [shareItem] = shareFormikRef.current.values.items;
    const { share_type, share_at } = shareItem;

    let i18nKey = 'sharing:shareSuccess';
    const date = moment.unix(share_at).format('dddd, MMMM Do, YYYY [at] h:mma');

    switch (share_type) {
      case ShareType.Auto:
        i18nKey = 'sharing:autoSuccess';
        break;
      case ShareType.Scheduled:
        i18nKey = 'sharing:scheduledSuccess';
        break;
      case ShareType.Now:
        i18nKey = 'sharing:shareSuccess';
        break;
      default:
        throw new Error('Missing ShareType implementation');
    }

    notify.info({ i18n: { i18nKey, values: { date } } });
    location.key ? navigate(-1) : navigate(paths.index);
  }

  function shareToTwitterAndLinkedin(content: ContentFragment) {
    const clonedItems = cloneDeep(shareFormikRef.current.values.items);
    const twitterAndLinkedInItems = clonedItems
      .filter(item => item.state.active)
      .filter(
        item =>
          item.state.networkMeta.provider === Network.Twitter ||
          item.state.networkMeta.provider === Network.Linkedin ||
          item.state.networkMeta.provider === Network.LinkedinPage
      );

    const photosToPost = content?.content_object?.photos ?? [];

    twitterAndLinkedInItems.forEach(item => {
      // Remove client side state
      delete item.state;

      // Explicitly pass photos from created content
      const photo = photosToPost[selectedPhotoIndex];
      if (photo) {
        item.photos = [photo];
      }
    });

    if (!twitterAndLinkedInItems.length) {
      return;
    }

    const variables = {
      timezone: shareFormikRef.current.values.timezone,
      content_id: content.content_id,
      items: twitterAndLinkedInItems,
    };

    shareContent(variables as ShareContentMutationVariables);
  }

  // We don't have tracking for Facebook posts
  function shareToFacebook(content: ContentFragment) {
    const clonedItems = cloneDeep(shareFormikRef.current.values.items);
    const facebookShareItem = clonedItems.find(item => {
      return item.state.networkMeta.provider === Network.Facebook;
    });

    if (!facebookShareItem?.state?.active) {
      return;
    }

    const fbUIVariables = {
      method: 'share',
      href: `https://${client.subdomain}/content/${content.content_id}`,
      quote: facebookShareItem.user_commentary,
    };

    delete facebookShareItem.state;

    window?.FB?.ui(fbUIVariables);
  }

  function onSubmit() {
    processContent({ content: postFormikRef.current.values });
  }

  return (
    <Formik
      onSubmit={() => undefined}
      initialValues={postFormikRef.current.initialValues}
      innerRef={ref => (postFormikRef.current = ref)}
      validationSchema={sharingSchema}
    >
      {children({
        isLoading,
        onSubmit,
        postFormikRef,
        shareFormikRef,
      })}
    </Formik>
  );
};

export default Form;
